import React, { useEffect, useState } from 'react';
import { TIKTOK_OAUTH_CALLBACK_URL } from '../../utils/constants';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';

const normalizeBaseUrl = (url) => (url || '').trim().replace(/\/+$/, '');

const buildCallbackUrl = (code, state, clientId) => {
  const callbackBaseUrl = normalizeBaseUrl(TIKTOK_OAUTH_CALLBACK_URL);
  if (!callbackBaseUrl) {
    return null;
  }

  return {
    baseUrl: callbackBaseUrl,
    payload: {
      code,
      state,
      clientId: clientId || undefined
    }
  };
};

const extractClientIdFromState = (state) => {
  // 1. sessionStorage — most reliable, saved in Links.js before the OAuth redirect
  try {
    const stored = sessionStorage.getItem('tiktok_oauth_clientId');
    if (stored) {
      sessionStorage.removeItem('tiktok_oauth_clientId');
      return stored;
    }
  } catch {
    // sessionStorage unavailable
  }

  // 2. Try URL-decoded then JSON-parsed state: {"clientId":"...","nonce":"...","issuedAt":...}
  try {
    const stateData = JSON.parse(decodeURIComponent(state));
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not URL-encoded JSON
  }

  // 3. Try raw JSON parse (state already decoded by browser)
  try {
    const stateData = JSON.parse(state);
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not raw JSON
  }

  // 4. Try base64-decoded JSON (common in OAuth state params)
  try {
    const decoded = atob(state.replace(/-/g, '+').replace(/_/g, '/'));
    const stateData = JSON.parse(decoded);
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not base64 JSON
  }

  return null;
};

const TikTokCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing TikTok authorization...');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || error);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage('Missing authorization data from TikTok.');
      return;
    }

    const clientId = extractClientIdFromState(state);
    const nextUrl = buildCallbackUrl(code, state, clientId);

    if (!nextUrl) {
      setStatus('error');
      setMessage(
        'TikTok backend callback URL is not configured. Check TIKTOK_OAUTH_CALLBACK_URL.'
      );
      return;
    }

    setMessage('TikTok authorized. Redirecting to backend callback...');

    // Use a top-level GET navigation to avoid CORS preflight with credentials.
    const timeoutId = window.setTimeout(() => {
      try {
        const callbackUrl = new URL(nextUrl.baseUrl);
        callbackUrl.searchParams.set('code', code);
        callbackUrl.searchParams.set('state', state);
        if (clientId) {
          callbackUrl.searchParams.set('clientId', clientId);
        }

        window.location.replace(callbackUrl.toString());
      } catch (err) {
        console.error('Callback redirect error:', err);
        setStatus('error');
        setMessage(`Connection failed: ${err.message}`);
      }
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {status === 'processing' && 'Connecting TikTok...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Connection Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            )}
            {status === 'success' && (
              <div className="text-green-600 text-5xl">OK</div>
            )}
            {status === 'error' && (
              <div className="text-red-600 text-5xl">X</div>
            )}
            <p className="text-center text-sm text-gray-600">{message}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TikTokCallback;
